// src/pages/Trend.tsx
import React, { useState, useMemo } from "react";
import Papa from "papaparse";
import { useData } from "../context/DataContext";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Match {
  date: string;
  [key: string]: any;
}

type TrendProps = {
  embed?: boolean;
  height?: number;
};

const palette = {
  bg: "#EEF5FF",
  panel: "#B4D4FF",
  border: "#86B6F6",
  accent: "#176B87",
  ink: "#0B2230",
  white: "#FFFFFF",
};

const Trend: React.FC<TrendProps> = ({ embed = false, height = 280 }) => {
  const { rows } = useData();
  const [view, setView] = useState<"chart" | "table">("chart");

  const matches = useMemo<Match[]>(
    () => rows.filter((r) => r.date) as Match[],
    [rows]
  );

  const yearCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    matches.forEach((m) => {
      const year = new Date(m.date).getFullYear();
      if (Number.isFinite(year)) {
        counts[year] = (counts[year] ?? 0) + 1;
      }
    });
    return counts;
  }, [matches]);

  const sortedYears = useMemo(
    () => Object.keys(yearCounts).map(Number).sort((a, b) => a - b),
    [yearCounts]
  );

  const chartData = useMemo(
    () => ({
      labels: sortedYears.map(String),
      datasets: [
        {
          label: "Matches per Year",
          data: sortedYears.map((y) => yearCounts[String(y)] ?? 0),
          borderColor: palette.accent,
          backgroundColor: "rgba(23,107,135,0.18)",
          pointRadius: embed ? 2 : 3,
          pointBackgroundColor: palette.accent,
          tension: 0.25,
        },
      ],
    }),
    [sortedYears, yearCounts, embed]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: !embed },
        tooltip: { enabled: true },
        title: embed
          ? undefined
          : {
              display: false,
              text: "Matches per Year",
            },
      },
      scales: {
        x: {
          ticks: { autoSkip: true, maxTicksLimit: embed ? 8 : 12, color: palette.ink },
          grid: { color: "rgba(134,182,246,0.35)" },
        },
        y: {
          beginAtZero: true,
          ticks: { color: palette.ink },
          grid: { color: "rgba(134,182,246,0.35)" },
        },
      },
    }),
    [embed]
  );

  const downloadCsv = () => {
    const csv = Papa.unparse(matches);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "international-rugby-results-1871-2022.csv";
    a.click();
  };

  if (embed) {
    return (
      <div style={{ width: "100%" }}>
        <div style={{ height }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, background: palette.bg, minHeight: "100vh", color: palette.ink }}>
      <h1 style={{ marginTop: 0, color: palette.accent }}>International Rugby Union Results (1871–2022)</h1>
      <p style={{ color: "rgba(11,34,48,0.75)" }}>
        Source dataset from Lyle Begbie on Kaggle.{" "}
        <a
          href="https://www.kaggle.com/datasets/lylebegbie/international-rugby-union-results-from-18712022"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: palette.accent, fontWeight: 800 }}
        >
          View on Kaggle
        </a>
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
        <button
          onClick={() => setView(view === "chart" ? "table" : "chart")}
          style={{
            padding: "10px 12px",
            borderRadius: 12,
            border: `1px solid ${palette.border}`,
            background: palette.panel,
            color: palette.ink,
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Toggle to {view === "chart" ? "Table View" : "Chart View"}
        </button>

        <button
          onClick={downloadCsv}
          style={{
            padding: "10px 12px",
            borderRadius: 12,
            border: `1px solid ${palette.border}`,
            background: palette.white,
            color: palette.accent,
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Download CSV
        </button>
      </div>

      {view === "chart" ? (
        <div
          style={{
            maxWidth: 900,
            marginTop: 20,
            height: 420,
            background: palette.white,
            border: `1px solid ${palette.border}`,
            borderRadius: 16,
            padding: 12,
            boxShadow: "0 10px 24px rgba(23,107,135,0.12)",
          }}
        >
          <Line data={chartData} options={chartOptions} />
        </div>
      ) : (
        <div
          style={{
            marginTop: 20,
            overflowX: "auto",
            background: palette.white,
            border: `1px solid ${palette.border}`,
            borderRadius: 16,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr style={{ background: palette.panel }}>
                <th style={{ borderBottom: `1px solid ${palette.border}`, padding: "10px 12px", textAlign: "left" }}>
                  Year
                </th>
                <th style={{ borderBottom: `1px solid ${palette.border}`, padding: "10px 12px", textAlign: "left" }}>
                  Match Count
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedYears.map((y) => (
                <tr key={y}>
                  <td style={{ borderBottom: `1px solid rgba(134,182,246,0.45)`, padding: "10px 12px" }}>{y}</td>
                  <td style={{ borderBottom: `1px solid rgba(134,182,246,0.45)`, padding: "10px 12px" }}>
                    {yearCounts[String(y)]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Trend;
